import { NavLink } from "react-router-dom";
import { Home, MessageSquare, Radio, MessageCircle, UserCheck, Gift, Users, Trophy, User, Shield, ExternalLink, Headphones } from "lucide-react";
import { useAuth } from "../context/AuthContext";
import { LOGO_URL, WEBSITE_URL } from "../lib/api";

const COMMUNITY_NAV = [
  { to: "/", label: "Startseite", icon: Home, testId: "nav-home" },
  { to: "/chat", label: "Chat", icon: MessageSquare, testId: "nav-chat" },
  { to: "/mitarbeiter", label: "Mitarbeiter", icon: Headphones, testId: "nav-support", supportAccent: true },
  { to: "/live", label: "Live", icon: Radio, testId: "nav-live" },
  { to: "/nachrichten", label: "DM", icon: MessageCircle, testId: "nav-messages" },
  { to: "/freunde", label: "Freunde", icon: UserCheck, testId: "nav-friends" },
  { to: "/verlosung", label: "Lose", icon: Gift, testId: "nav-giveaway" },
  { to: "/mitglieder", label: "Mitglieder", icon: Users, testId: "nav-members" },
  { to: "/rangliste", label: "Rangliste", icon: Trophy, testId: "nav-leaderboard" },
  { to: "/profil", label: "Profil", icon: User, testId: "nav-profile" },
];

const CATEGORIES = [
  { id: "allgemein", label: "Allgemein" },
  { id: "routen", label: "Routen" },
  { id: "probleme", label: "Probleme" },
  { id: "marktplatz", label: "Marktplatz" },
  { id: "smalltalk", label: "Smalltalk" },
];

const REGIONS = [
  "Nord", "Ost", "West", "Mitte", "Süd",
  "Berlin", "München", "Hamburg", "Köln/Düsseldorf", "Frankfurt",
  "Österreich", "Schweiz",
];

const REGION_SLUG = {
  "Nord": "region-nord", "Ost": "region-ost", "West": "region-west", "Mitte": "region-mitte", "Süd": "region-sued",
  "Berlin": "region-berlin", "München": "region-muenchen", "Hamburg": "region-hamburg",
  "Köln/Düsseldorf": "region-koeln", "Frankfurt": "region-frankfurt",
  "Österreich": "region-at", "Schweiz": "region-ch",
};

export default function LeftSidebar({ onNavigate }) {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";

  return (
    <aside className="w-full lg:w-60 xl:w-64 shrink-0 space-y-5" data-testid="left-sidebar">
      {/* Section: Community */}
      <NavSection title="Community">
        {COMMUNITY_NAV.map((item) => (
          <SidebarLink key={item.to} {...item} onClick={onNavigate} />
        ))}
        {isAdmin && (
          <SidebarLink to="/admin" label="Admin" icon={Shield} testId="nav-admin" onClick={onNavigate} />
        )}
      </NavSection>

      {/* Section: Kategorien */}
      <NavSection title="Kategorien" decoration="#">
        {CATEGORIES.map((c) => (
          <CategoryLink key={c.id} to={`/chat?channel=${c.id}`} label={c.label} onClick={onNavigate} testId={`sb-cat-${c.id}`} />
        ))}
      </NavSection>

      {/* Section: Regionen */}
      <NavSection title="Regionen" decoration="#">
        {REGIONS.map((name) => (
          <CategoryLink key={name} to={`/chat?channel=${REGION_SLUG[name]}`} label={name} onClick={onNavigate} testId={`sb-reg-${REGION_SLUG[name]}`} />
        ))}
      </NavSection>

      {/* CTA Card */}
      <div className="bg-gradient-to-br from-[#1D5BFF]/20 to-[#FFFFFF] border border-[#1D5BFF]/30 rounded-2xl p-4 relative overflow-hidden">
        <div className="absolute -bottom-6 -right-6 opacity-40">
          <img src={LOGO_URL} alt="" className="w-24 h-24 object-contain" />
        </div>
        <div className="relative">
          <div className="text-[10px] uppercase tracking-[0.2em] text-[#4DBFFF] font-bold">TSU-PARTNERS</div>
          <div className="font-display text-base font-black text-slate-900 mt-1 leading-tight">
            Tausche dich live aus –<br />
            <span className="text-[#4DBFFF]">mit der TSU-Family.</span>
          </div>
          <a
            href={WEBSITE_URL}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="sb-website-link"
            className="inline-flex items-center gap-1.5 mt-3 bg-[#1D5BFF] hover:bg-[#1546C9] text-white text-xs font-bold px-3 py-1.5 rounded-full transition"
          >
            Zur Website <ExternalLink className="w-3 h-3" />
          </a>
        </div>
      </div>
    </aside>
  );
}

function NavSection({ title, decoration, children }) {
  return (
    <div>
      <div className="text-[10px] uppercase tracking-[0.2em] text-slate-500 font-bold px-3 mb-1.5 flex items-center gap-1">
        {decoration && <span className="text-[#4DBFFF]">{decoration}</span>}
        {title}
      </div>
      <nav className="space-y-0.5">{children}</nav>
    </div>
  );
}

function SidebarLink({ to, label, icon: Icon, testId, onClick, accent, supportAccent }) {
  return (
    <NavLink
      to={to}
      onClick={onClick}
      data-testid={`sb-${testId}`}
      end={to === "/"}
      className={({ isActive }) =>
        `flex items-center gap-2.5 px-3 py-2 rounded-xl text-sm font-semibold transition ${
          isActive
            ? "btn-brand text-slate-900 shadow-[0_4px_20px_rgba(29,91,255,0.4)]"
            : accent
              ? "text-[#4DBFFF] hover:text-slate-900 hover:bg-slate-100/70"
              : supportAccent
                ? "text-emerald-300 hover:text-slate-900 hover:bg-slate-100/70"
                : "text-slate-700 hover:text-slate-900 hover:bg-slate-100/70"
        }`
      }
    >
      {Icon && <Icon className="w-4 h-4 shrink-0" />}
      <span className="truncate">{label}</span>
      {accent && (
        <span className="ml-auto text-[8px] uppercase font-bold px-1.5 py-0.5 rounded bg-[#4DBFFF]/20 text-[#4DBFFF]">KI</span>
      )}
      {supportAccent && (
        <span className="ml-auto text-[8px] uppercase font-bold px-1.5 py-0.5 rounded bg-emerald-500/20 text-emerald-300">Team</span>
      )}
    </NavLink>
  );
}

function CategoryLink({ to, label, onClick, testId }) {
  return (
    <NavLink
      to={to}
      onClick={onClick}
      data-testid={testId}
      className={({ isActive }) =>
        `flex items-center gap-2 px-3 py-1.5 rounded-lg text-sm transition ${
          isActive ? "bg-slate-100 text-slate-900" : "text-slate-600 hover:text-slate-900 hover:bg-slate-100/70"
        }`
      }
    >
      <span className="text-[#4DBFFF]">#</span>
      <span className="truncate">{label}</span>
    </NavLink>
  );
}
